import { useState } from 'react';
import {
  CardDeliveryItem,
  SelectLink,
  Text
} from '@cencosud-cencommerce/paris-uikit';
import { type TimeSlot } from '@cencosud-cencommerce/paris-uikit/dist/molecules/Cards/CardDeliveryInfo/interfaces';
import type { Value } from '@cencosud-cencommerce/paris-uikit/dist/molecules/DatePicker/DatePicker';

import { useAppDispatch, useAppSelector } from '@/Application/Hooks/Redux';
import { selectBuyNowCartData } from '@/Application/Selectors/buyNow/useBuyNowCart';
import {
  setIsDatePickerOpen,
  setScheduledDate
} from '@/modules/buy-now/infrastructure/slices';
import {
  BuyNowEventInteractiveCategories,
  BuyNowEventInteractiveTags
} from '@/modules/shared/utils/gtmEvents/buyNowTypes';
import { sendEvent } from '@/modules/shared/utils/gtmEvents/sendEvents/sendBuyNowEvents';
import type {
  DeliveryGroup,
  TimeSlotValue
} from '@/modules/shippingMethod/domain/ShippingMethod';
import type { HttpClientPreferenceParams } from '@/modules/shippingMethod/infrastructure/dto/HttpShippingMethodDto';
import { CardDeliveryInfoVariants } from '@/Pages/BuyNowPage/PaymentAndShipping/interfaces';
import {
  DEFAULT_TIME_SLOTS,
  LABEL_WHEN_DO_YOU_WANT_TO_RECEIVE
} from '@/Pages/BuyNowPage/PaymentAndShipping/partials/ShippingOptions/constants';
import { DateSelectionModal } from '@/Pages/BuyNowPage/PaymentAndShipping/partials/ShippingOptions/DateSelectionModal';

interface ShippingOptionsProps extends DeliveryGroup {
  handleSelectClientSelection: (
    deliveryGroupId: string,
    params?: HttpClientPreferenceParams,
    scheduledDateValue?: Date | null
  ) => Promise<void>;
  handleCalendarDateConfirm: (scheduledDateValue: Date) => Promise<void>;
  deliveryGroupId: string;
}

interface TimeFrame extends TimeSlotValue {
  isInitialTimeFrame: boolean;
  id: string;
}

const LABEL_TH = 'Entre 09:00 y 21:00';

const getVariant = (shippingMethod: string) => {
  switch (shippingMethod) {
    case 'SAME_DAY':
      return CardDeliveryInfoVariants.SAME_DAY;
    case 'NEXT_DAY':
      return CardDeliveryInfoVariants.NEXT_DAY;
    case 'DATE_RANGE':
      return CardDeliveryInfoVariants.DATE_RANGE;
    case 'SCHEDULED':
      return CardDeliveryInfoVariants.SCHEDULED;
    case 'SCHEDULED_DEFERRED':
      return CardDeliveryInfoVariants.SCHEDULED_DEFERRED;
    default:
      return CardDeliveryInfoVariants.SCHEDULED_DEFERRED;
  }
};

const extractId = (id: string) => {
  const match = id.match(/^delivery-([a-z0-9-]+?)-\d+$/);
  return match ? match[1] : id;
};

export const ShippingOptions = ({
  id,
  shippingOptions,
  scheduledOptions,
  classificationGroupMeta,
  handleSelectClientSelection,
  handleCalendarDateConfirm
}: ShippingOptionsProps) => {
  const dispatch = useAppDispatch();
  const buyNowCart = useAppSelector(selectBuyNowCartData);
  const email = buyNowCart?.shippingAddress?.email || '';
  const [scheduleSelectedDate, setScheduleSelectedDate] =
    useState<Value | null>(null);

  const handleTimeSlotClick = (clickedTimeSlotName: string) => {
    const updatedTimeSlots = selectedTimeSlot.map((timeSlot) => ({
      ...timeSlot,
      selected: timeSlot?.name === clickedTimeSlotName,
      onClick: () => {
        handleTimeSlotClick(timeSlot?.name);
        sendEvent({
          event: 'baseEvent',
          extraFields: {
            'event-interactive-tag': `horario envio ${timeSlot?.deliveryTimeStart ?? ''} - ${timeSlot?.deliveryTimeEnd ?? ''}`,
            'event-interactive-category':
              BuyNowEventInteractiveCategories.FastCheckout
          }
        });
      }
    }));

    setSelectedTimeSlot(updatedTimeSlots);
  };

  const initializeTimeSlots = (timeSlots: TimeSlot[]) => {
    return timeSlots.map((timeSlot) => ({
      ...timeSlot,
      onClick: () => {
        handleTimeSlotClick(timeSlot?.name);
        sendEvent({
          event: 'baseEvent',
          extraFields: {
            'event-interactive-tag': `horario envio ${timeSlot?.deliveryTimeStart ?? ''} - ${timeSlot?.deliveryTimeEnd ?? ''}`,
            'event-interactive-category':
              BuyNowEventInteractiveCategories.FastCheckout
          }
        });
      }
    }));
  };

  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot[]>(() =>
    initializeTimeSlots(DEFAULT_TIME_SLOTS)
  );

  const selectedShippingOption = shippingOptions?.find((opt) => opt.selected);
  const isSameDayDelivery =
    selectedShippingOption?.shippingMethod === 'SAME_DAY';
  const sameDayId = isSameDayDelivery
    ? extractId(selectedShippingOption?.id)
    : '';

  const [timeFrame] = useState<TimeFrame | null>(
    isSameDayDelivery && selectedShippingOption
      ? (() => {
          const matchedChip = selectedShippingOption?.chips?.find(
            (chip) =>
              chip?.value?.timeSlot?.name ===
              classificationGroupMeta?.timeSlotIndex
          );

          const selectedChip =
            matchedChip || selectedShippingOption?.chips?.[0];

          return {
            isInitialTimeFrame: true,
            id: sameDayId,
            shipDate: selectedChip?.value.shipDate as string,
            timeSlot: selectedChip?.value.timeSlot
          };
        })()
      : null
  );

  if (!shippingOptions) return null;

  const handleDateChange = (optionId: string) => {
    const selectedOption = shippingOptions?.find((opt) => opt.id === optionId);
    if (selectedOption) {
      handleSelectClientSelection(id, {
        shippingType: selectedOption?.shippingType,
        shippingSubType: selectedOption?.shippingSubType,
        shippingMethod: selectedOption?.shippingMethod,
        deliveryDateFrom:
          selectedOption?.deliveryDateFrom?.split('T')?.[0] ?? undefined,
        deliveryDateTo:
          selectedOption?.deliveryDateTo?.split('T')?.[0] ?? undefined
      });
    }
  };

  const handleSelectAnotherDate = () => {
    sendEvent({
      event: 'baseEvent',
      extraFields: {
        'event-interactive-tag': BuyNowEventInteractiveTags.ElegirOtraFecha
      }
    });
    dispatch(setIsDatePickerOpen(true));
  };

  const dateList = shippingOptions?.map((date) => {
    const {
      id,
      shippingType,
      shippingSubType,
      shippingMethod,
      deliveryDateFrom,
      deliveryDateTo
    } = date;
    const deliveryGroupId = extractId(id);

    return {
      ...date,
      selected: shippingOptions.length !== 1 ? date?.selected : true,
      onChange: (id: string) => handleDateChange(id),
      chips: date?.chips?.map((chip) => {
        const { value } = chip;

        return {
          ...chip,
          onClick: () => {
            if (!value?.timeSlot) return;

            sendEvent({
              event: 'baseEvent',
              extraFields: {
                'event-interactive-tag': `horario envio ${value?.timeSlot?.deliveryTimeStart ?? ''} - ${value?.timeSlot?.deliveryTimeEnd ?? ''}`,
                'event-interactive-category':
                  BuyNowEventInteractiveCategories.FastCheckout
              }
            });

            handleSelectClientSelection(deliveryGroupId, {
              shippingType: shippingType,
              shippingSubType: shippingSubType,
              shippingMethod: shippingMethod,
              deliveryDateFrom: deliveryDateFrom?.split('T')?.[0] ?? undefined,
              deliveryDateTo: deliveryDateTo?.split('T')?.[0] ?? undefined,
              timeSlot: value?.timeSlot?.name
            });
          },
          selected: value?.timeSlot?.name === timeFrame?.timeSlot?.name
        };
      })
    };
  });

  const shippingProps = {
    title: '¿Cuándo quieres recibir tu compra?',
    dateList,
    onChooseDate: () => {
      dispatch(setIsDatePickerOpen(true));
    }
  };

  const hasOnlyOneScheduleOption = scheduledOptions?.some(
    (opt) => opt?.options?.length === 1
  );
  const isSelectAnotherDateAvailable =
    !!scheduledOptions?.length && !hasOnlyOneScheduleOption;

  return (
    <>
      <fieldset
        className="co-flex co-flex-col co-w-full"
        data-testid="co-delivery-date-selector"
      >
        <Text variant="body2" weight={600} className="co-p-4">
          {LABEL_WHEN_DO_YOU_WANT_TO_RECEIVE}
        </Text>

        {shippingProps?.dateList?.map((option) => {
          const deliveryDateFrom = option?.deliveryDateFrom;
          const deliveryDateTo = option?.deliveryDateTo;
          const deliveryPrice = option?.price;
          const variant = getVariant(option?.shippingMethod);

          const isSelected = option?.selected;
          if (!deliveryDateFrom) return null;

          return (
            <article
              key={option?.shippingMethod}
              className="co-w-full co-border-t co-border-t-neutral-300"
            >
              <CardDeliveryItem
                deliveryDate={new Date(deliveryDateFrom)}
                deliveryDateEnd={
                  deliveryDateTo ? new Date(deliveryDateTo) : undefined
                }
                deliveryPrice={deliveryPrice ?? ''}
                variant={variant}
                isFlash={variant === CardDeliveryInfoVariants.SAME_DAY}
                crossedOutPrice={option?.crossedOutPrice ?? undefined}
                isFreeDelivery={!deliveryPrice || deliveryPrice === '0'}
                userEmail={email}
                timeSlots={option.chips
                  ?.filter((chip) => chip.value.timeSlot)
                  .map((chip) => ({
                    deliveryTimeStart: chip.value.timeSlot!.deliveryTimeStart,
                    deliveryTimeEnd: chip.value.timeSlot!.deliveryTimeEnd,
                    name: chip.value.timeSlot!.name as TimeSlot['name'],
                    disabled: chip.disabled,
                    selected: chip.selected,
                    onClick: chip.onClick
                  }))}
                labelTH={LABEL_TH}
                radioButton={{
                  checked: isSelected,
                  name: 'selectedShippingOption',
                  onChange: () => {
                    setScheduleSelectedDate(null);
                    handleSelectClientSelection(id, {
                      shippingType: option?.shippingType,
                      shippingSubType: option?.shippingSubType,
                      shippingMethod: option?.shippingMethod,
                      deliveryDateFrom:
                        deliveryDateFrom?.split('T')?.[0] ?? undefined,
                      deliveryDateTo:
                        deliveryDateTo?.split('T')?.[0] ?? undefined
                    });
                  }
                }}
              />
            </article>
          );
        })}

        {isSelectAnotherDateAvailable && (
          <div className="co-w-full co-border-t co-border-t-neutral-300">
            <SelectLink
              iconName="il-book-service"
              label="Elegir otra fecha"
              onClick={handleSelectAnotherDate}
              className="co-p-4"
            />
          </div>
        )}
      </fieldset>

      <DateSelectionModal
        scheduleSelectedDate={scheduleSelectedDate}
        setScheduleSelectedDate={setScheduleSelectedDate}
        setSelectedDate={(value) => dispatch(setScheduledDate(value))}
        scheduledOptions={scheduledOptions}
        onConfirm={(date) => {
          handleCalendarDateConfirm(date as Date);
        }}
      />
    </>
  );
};

ShippingOptions.displayName = 'ShippingOptions';

export default ShippingOptions;
